<script>
  import { onDestroy, onMount } from 'svelte';
  import { readable } from 'svelte/store';
  import { tweened } from 'svelte/motion';
  import { cubicOut } from 'svelte/easing';
  import Slider from '@bulatdashiev/svelte-slider';

  import { scaleLinear } from 'd3-scale';

  import { create_interpolator, interpolate } from './cspl.js';
  import { linspace, normalize } from './util.js';
  import { project_onto_pib_states } from './qpib.js';
  import { pannable } from './pannable.js';
  import Modal from './help.svelte'

  import { Parser } from 'expr-eval';

  // potentially read defaults from URLSearchParams
  const query = window.location.search;
  const url_parameters = new URLSearchParams(query);

  const get_optional = (key, deflt) => {
    if (url_parameters.has(key)) {
      return url_parameters.get(key);
    } else {
      return deflt;
    }
  };

  let svg;
  let windowHeight = 300;
  let windowWidth = 500;
  $: width = windowWidth;
  $: height = 300;

  let show_help = false;
  const show_coeffs = get_optional("show_coeffs", false);

  const resizeSVG = () => {
    let svgBoundingBox = svg.getBBox();

    width = svgBoundingBox.x + svgBoundingBox.width + svgBoundingBox.x;
    height = svgBoundingBox.y + svgBoundingBox.height + svgBoundingBox.y;
  };

  onMount(resizeSVG);

  let func_str = null;

  let animate = false;

  let frame;
  let t0 = window.performance.now();
  let time = 0;

  // slider default value: slider maps [-4:4] to a log range from 1/16 to 16x
  let speed_factor = 0;
  $: base_speed = period_of_motion / 5000 // let one period of motion take 5s
  $: speed = base_speed * Math.pow(2, speed_factor);

  (function update() {
		frame = requestAnimationFrame(update);

		const now = window.performance.now();
    if (animate) {
      time += speed * (now - t0);
    }

		t0 = now;
	}());

	onDestroy(() => {
		cancelAnimationFrame(frame);
	});

  // physical constants
  let H2eV = 27.2114;
  let au2fs = 0.02418884254;
  let nm2bohr = 18.8973

  // physical parameters
  let mass = get_optional("mass", 1.0); // electron mass in a.u.
  let boxlength = get_optional("boxlength", 1.0); // box lenght in nm
  $: boxlength_bohr = boxlength * nm2bohr;

  // derived parameters
  $: k_au = Math.PI / boxlength_bohr;
  $: k_nm = Math.PI / boxlength;
  $: omega_au = 0.5 * k_au * k_au / mass;
  $: omega_ev = omega_au * H2eV;
  $: period_of_motion = 2.0 * Math.PI / omega_au;

  // numerical parameters
  let nx = get_optional("nx", 128);

  let adjusting = false;
  let mode = "edit"

  function create_interpolant(control_points) {
    let sorted_cp = control_points.slice().sort(function (a, b) { return a.x - b.x; });
    let xs = sorted_cp.map(cp => { return cp.x; })
    let ys = sorted_cp.map(cp => { return cp.y; })

    return create_interpolator(xs, ys);
  }

  function add_dot(event) {
    if (adjusting) {
      adjusting = false;
      return;
    }
    if (animate || (mode=="run")) {
     return
    }

    const cp = {
      x: xScale.invert(event.offsetX),
      y: yScale.invert(event.offsetY)
    }

    control_points = control_points.concat(cp).sort(function (a, b) { return a.x - b.x });
    interpolant = create_interpolant(control_points);
  }

  $: get_psi2_points_at_time = (t) => {
    let out = [];
    let x;
    let nphi = cn.length;
    for (x = 0; x < nx; x++) {
      let xx = boxlength * x/(nx-1);
      let val = 0.0;
      let i, j, wij;
      for (i = 0; i < nphi; i++) {
        val += cn[i] * cn[i] * Math.sin((i+1)*k_nm*xx)**2;
        for (j = 0; j < i; j++) {
          wij = omega_au * (j*j - i*i);
          val += cn[i] * cn[j] * 2.0 * Math.sin((i+1)*k_nm*xx) * Math.sin((j+1)*k_nm*xx) * Math.cos(wij * t);
        }
      }
      val *= 2 / boxlength; // for normalization of eigenstates
      out.push({x: xx, y:val});
    }
    return out;
  }

  $: get_psi_points = () => {
    let out = [];
    let x;
    let nphi = cn.length;
    for (x = 0; x < nx; x++) {
      let xx = boxlength * x/(nx-1);
      let val = 0.0;
      let i, j;
      for (i = 0; i < nphi; i++) {
        val += cn[i] * Math.sin((i+1)*k_nm*xx);
      }
      val *= Math.sqrt(2 / boxlength); // for normalization of eigenstates
      out.push({x: xx, y:val});
    }
    return out;
  }

  $: get_points_at_time = (t) => {
    if (mode == "edit") {
      return get_psi_points();
    }
    else {
      return get_psi2_points_at_time(t);
    }
  }

  $: points = get_points_at_time(time);

  $: get_smoothed_curve = (control_points) => {
    let xx = linspace(0.0, boxlength, nx);
    let yy = interpolate(interpolant, xx);

    let i;
    let out = []
    for (i = 0; i < xx.length; ++i) {
      out.push( { x: xx[i], y: yy[i] } );
    }
    return out;
  };

  let minX = 0.0;
  let maxX = boxlength;

  $: yTicks = (mode=="edit") ? [-2, -1, 0, 1, 2] : [0, 1, 2, 3, 4];
  const xTicks = [0*boxlength, 0.25*boxlength, 0.5*boxlength, 0.75*boxlength, boxlength];
  const padding = { top: 20, right: 15, bottom: 20, left: 25 };

  $: xScale = scaleLinear()
    .domain([minX, maxX])
    .range([padding.left, width - padding.right]);

  $: yScale = scaleLinear()
    .domain([Math.min.apply(null, yTicks), Math.max.apply(null, yTicks)])
    .range([height - padding.bottom, padding.top]);

  let control_points = url_parameters.has("control_points") ?
    JSON.parse(url_parameters.get("control_points")) : [ { x: 0, y: 0 }, {x:0.5*boxlength, y:1}, { x: boxlength, y: 0 } ]

  $: interpolant = create_interpolant(control_points);
  $: smoothed_points = get_smoothed_curve(control_points);
  $: smoothed_path =`M${smoothed_points.map(p => `${xScale(p.x)},${yScale(p.y)}`).join('L')}`;
  $: cn = project_onto_pib_states(smoothed_points, k_nm, 1e-5);
  $: cn_normal = normalize(cn)
  $: path = `M${points.map(p => `${xScale(p.x)},${yScale(p.y)}`).join('L')}`;
  $: area = `${path}L${xScale(minX)},${yScale(0)}L${xScale(maxX)},${yScale(0)}Z`;
  $: control_path = `M${control_points.map(cp => `${xScale(cp.x)},${yScale(cp.y)} ${xScale(cp.x)},${yScale(cp.y)}`).join('Q')}`;
  $: normalize_control_points = (sp, cp) => {
    let nrm = 0.0;
    let dx = sp[1].x - sp[0].x;
    sp.forEach( (x) => { nrm += x.y * x.y } );
    nrm *= dx;

    nrm = 1.0 / Math.sqrt(nrm);
    cp.forEach( (x) => { x.y *= nrm } );
    return cp
   };

  function formatMobile (tick) {
    return "'" + tick.toString().slice(-2);
  }
</script>

<h2>Quantum Particle in a Box</h2>

<div class="row">
<button on:click={() => {
    mode = "edit";
    animate = false;
    time = 0.0;
  }} style="background-color: { mode == "edit" ? "green" : "" }">
  Edit Wavefunction
</button>
<button on:click={() => {
  control_points = normalize_control_points(smoothed_points, control_points);
  mode = "run";
  }} style="background-color: { mode == "run" ? "green" : "" }">
  Simulate
</button>
<button on:click="{() => show_help = true}">
	Help
</button>
</div>
<br>

<div class="chart" on:click={add_dot} bind:clientWidth={windowWidth} bind:clientHeight={windowHeight}>
  <svg bind:this={svg} viewBox="0 0 {width} {height}" width={width} height={height}>
    <!-- y axis -->
    <g class="axis y-axis" transform="translate(0, {padding.top})">
      {#each yTicks as tick}
        <g class="tick tick-{tick}" transform="translate(0, {yScale(tick) - padding.bottom})">
          <line x2="100%"></line>
          <text y="-4">{tick} {tick === 8 ? ' |psi(x)|^2' : ''}</text>
        </g>
      {/each}
    </g>

    <!-- x axis -->
    <g class="axis x-axis">
      {#each xTicks as tick}
        <g class="tick tick-{ tick }" transform="translate({xScale(tick)},{height})">
          <line y1="-{height}" y2="-{padding.bottom}" x1="0" x2="0"></line>
          <text y="-2">{width > 380 ? tick : formatMobile(tick)}</text>
        </g>
      {/each}
    </g>

    <!-- data -->
    <path class="path-area-{mode}" d={area}></path>
    <path class="path-line-{mode}" d={path}></path>

    {#if mode=="edit"}
    <path class="path-line-{mode}" d={smoothed_path}></path>

    <!-- control points -->
    {#each control_points as cp, i}
    <circle cx={xScale(cp.x)} cy={yScale(cp.y)} r=8 fill="black"
      use:pannable
      on:panstart={() => {adjusting = true;} }
      on:panmove={(event) => {
        let xx = cp.x
        cp.x = xScale.invert(event.detail.x);
        cp.y = yScale.invert(event.detail.y);

        interpolant = create_interpolant(control_points);
      }}
      on:panend={() => { control_points = control_points.sort(function (a, b) { return a.x - b.x; }); } }
      on:dblclick="{() => { control_points.splice(i, 1); control_points = control_points.filter( x => { return true; }); } }" />
    {/each}
    {/if}
  </svg>
</div>

{#if mode=="run"}
<div class="row">
<button on:click={() => { animate = !animate; }}>
  {(animate) ? 'Pause' : 'Play'}
</button>
<button on:click={() => { time = 0; }}> Reset </button>
<div>
	time: <span style="width: 72px; display: inline-block; text-align: right;">{(time * au2fs).toFixed(1)}</span>fs
</div>
<label>
	period of motion:
	<progress value="{time / period_of_motion - Math.floor(time / period_of_motion)}"></progress>
</label>

<div class="title">
	Simulation speed: {#if speed_factor < 0 } 1/{/if}{Math.pow(2,Math.abs(speed_factor))}
</div>
<label>
	<input type=range bind:value={speed_factor} min="-4" max="4" step={1}>
</label>
</div>

{:else if mode=="edit"}

<div class="row">
<button on:click={() => { control_points = normalize_control_points(smoothed_points, control_points); }}>
  Normalize </button>
<button on:click={() => { control_points = [ { x: 0, y: 0 }, { x: boxlength, y: 0 } ];}}>
 Clear </button>
<button on:click={() => {
  let urlparam = new URLSearchParams();
  let json = JSON.stringify(control_points);
  urlparam.append("control_points", JSON.stringify(control_points));
  let url = window.location.origin + window.location.pathname;
  let link = url + "?" + urlparam.toString();
  navigator.clipboard.writeText(link);

  alert("Copied the link: " + link);
}}>
Copy Link
</button>
</div>

<div class="row"><h3>Preset Wavefunctions</h3></div>
<div class="row">
<button on:click={() => {
  let nn = 10;
  control_points = [];
  for (let i = 0; i <= nn; i++) {
    let x = boxlength * i / nn;
    let y = Math.sin(k_nm * x) + Math.sin(2.0 * k_nm * x);
    y /= Math.sqrt(boxlength);
    control_points = control_points.concat({ x: x, y: y });
  }
  control_points = control_points;
  }}>
  <math xmlns="http://www.w3.org/1998/Math/MathML" style="color:rgba(0,0,0,255);color:rgb(0,0,0);"><mstyle><mrow><mtable columnspacing="0.167em" columnalign="right center left" displaystyle="true"><mtr><mtd><mo>(</mo><mrow><msub><mi>&#x3C6;</mi><mn>1</mn></msub></mrow><mo>+</mo><mrow><msub><mi>&#x3C6;</mi><mn>2</mn></msub></mrow><mo>)</mo><mo>/</mo><msqrt><mrow><mn>2</mn></mrow></msqrt></mtd></mtr></mtable></mrow></mstyle></math>
  </button>

<button on:click={() => {
  let n1 = 15;
  let n2 = 15;
  control_points = [];
  for (let i = 0; i < n1; i++) {
    let x = boxlength * (0.5 * i / n1);
    let y = 2.0 / Math.sqrt(boxlength) * Math.sin(2.0 * k_nm * x);
    control_points = control_points.concat({ x: x, y: y });
  }
  for (let i = 0; i <= n2; i++) {
    let x = boxlength * (0.5 + (0.5 * i / n2));
    let y = 0.0;
    control_points = control_points.concat({ x: x, y: y });
  }

  control_points = control_points;
  }}>
  Half-box
  </button>
</div>

<div class="row"><h3>Initialize from Function</h3></div>
<div class="row">
<input bind:value={func_str}>
<button on:click={() => {

  const parser = new Parser();
  parser.functions.phi = function(n, x) {
    return Math.sqrt(2.0 / boxlength) * Math.sin(n * k_nm * x);
  }
  let expr = parser.parse(func_str);

  let nn = 20;
  control_points = [];
  for (let i = 0; i <= nn; i++) {
    let x = boxlength * i / nn;
    let y = expr.evaluate({x: x, L: boxlength, m: mass})
    control_points = control_points.concat({ x: x, y: y });
  }
  control_points = control_points;
}}>Set Function</button>
</div>
{/if}

{#if show_help}
	<Modal on:close="{() => show_help = false}">
		<h2 slot="header"> Welcome to the Quantum Particle in a Box Simulator! </h2>
    <p>The simulator has two operating modes: editing and simulating. Let's get
    started with editing.</p>
    <h3>Preparing a wavefunction</h3>
    <ul>
      <li>draw a wavefunction:<ul>
         <li>click on the canvas to add a point</li>
         <li>click and drag to move a point</li>
         <li>double-click to remove a point</li>
      </ul>
      </li>
      <li>write a function:<ul>
         <li>type a function of x in the text box</li>
         <li>accepts standard functions (cos, sin, sqrt) and constants (PI, E)</li>
         <li>the function "phi(n,x)" produces the eigenfunction with quantum number n</li>
         <li>physical parameters:
            <ul>
              <li>L - box length</li>
              <li>m - physical mass</li>
            </ul>
         </li>
         <li>click "Set Function" when you're done</li>
         <li>For example, try inputing "x * (L - x)"</li>
      </ul>
      </li>
      <li>load a preset:
      <ul>
        <li>superposition of ground and first excited state</li>
        <li>constrained to the left half of the box</li>
      </ul>
      </li>
      <li>Normalize (note, this gets automatically done before simulations can start)</li>
      <li>Clear the current wavefunction and start fresh</li>
      <li>share your wavefunction by clicking the "Copy Link" button</li>
    </ul>
    <h3>Running a simulation</h3>
    <ul>
      <li>the "Play"/"Pause" button play/pause the simulation</li>
      <li>the "Reset" button sets time back to zero to resume from the initial wavefunction</li>
      <li>the speed slider adjusts the playback speed anywhere between 1/16 speed to 16x speed</li>
	</Modal>
{/if}

{#if show_coeffs}
<div class="row">
<p> Normalized Coefficients: </p>
<p>
{#each cn_normal as c, i}
<i>c</i><sub>{i+1}</sub> = {new Intl.NumberFormat('en-IN', { maximumSignificantDigits: 3}).format(c)} <br>
{/each}
</p>
</div>
{/if}

<style>
  .chart, h2, p {
    width: 100%;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }

  .row {
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }

  svg {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  .tick {
    font-size: .725em;
    font-weight: 200;
  }

  .tick line {
    stroke: #aaa;
    stroke-dasharray: 2;
  }

  .tick text {
    fill: #666;
    text-anchor: start;
  }

  .tick.tick-0 line {
    stroke-dasharray: 0;
  }

  .x-axis .tick text {
    text-anchor: middle;
  }

  .path-line-edit {
    fill: none;
    stroke: rgb(100,0,100);
    stroke-linejoin: round;
    stroke-linecap: round;
    stroke-width: 2;
  }

  .path-area-edit {
    fill: rgba(100,0,100,0.2);
  }

  .path-line-run {
    fill: none;
    stroke: rgb(0,100,100);
    stroke-linejoin: round;
    stroke-linecap: round;
    stroke-width: 2;
  }

  .path-area-run {
    fill: rgba(0,100,100,0.2);
  }

</style>



